/**
 * Landing Page — DataFlow Enterprise (welcome /welcome)
 * Conteúdo e layout baseados no pacote site.zip
 */

import React, { useCallback, useEffect, useState } from 'react';
import { ArrowUp } from 'lucide-react';
import { LeadCaptureModal } from './landing/LeadCaptureModal';
import type { LeadIntent } from './landing/leadQualification';
import { SiteHeader } from './landing/site/Header';
import { SiteHeroSection } from './landing/site/HeroSection';
import { SiteTrustMetricsBar } from './landing/site/TrustMetricsBar';
import { SiteSolutionsSection } from './landing/site/SolutionsSection';
import { SiteScoreSection } from './landing/site/ScoreSection';
import { SitePlatformSection } from './landing/site/PlatformSection';
import { SiteSecuritySection } from './landing/site/SecuritySection';
import { SiteContactSection } from './landing/site/ContactSection';
import { SiteFooter } from './landing/site/Footer';

const PAGE_TITLE = 'DataFlow — Engenharia, Simulação e Governança de Processos';
const PAGE_DESCRIPTION =
  'Plataforma enterprise para engenharia, simulação e governança de processos: torre de controle, modelagem visual, gestão da rotina, resolução de falhas, desdobramento estratégico e score de performance com RBAC e conformidade LGPD.';

function forceLandingLightTheme() {
  const html = document.documentElement;
  const body = document.body;
  const snapshot = {
    theme: html.getAttribute('data-theme'),
    hadDark: html.classList.contains('dark'),
    bodyClasses: [...body.classList],
    colorScheme: html.style.colorScheme,
  };
  html.classList.remove('dark');
  html.setAttribute('data-theme', 'light');
  html.style.colorScheme = 'light';
  body.classList.remove('theme-midnight', 'theme-black', 'theme-dark');
  body.classList.add('theme-light');
  body.style.colorScheme = 'light';
  return () => {
    if (snapshot.hadDark) html.classList.add('dark');
    if (snapshot.theme) html.setAttribute('data-theme', snapshot.theme);
    else html.removeAttribute('data-theme');
    html.style.colorScheme = snapshot.colorScheme || '';
    body.className = snapshot.bodyClasses.join(' ');
    body.style.colorScheme = '';
  };
}

export const LandingPage: React.FC = () => {
  const [leadModalOpen, setLeadModalOpen] = useState(false);
  const [leadIntent, setLeadIntent] = useState<LeadIntent>('demo');
  const [showBackToTop, setShowBackToTop] = useState(false);

  const openDemo = useCallback(() => {
    setLeadIntent('demo');
    setLeadModalOpen(true);
  }, []);

  const openDiagnosis = useCallback(() => {
    setLeadIntent('diagnosis');
    setLeadModalOpen(true);
  }, []);

  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    history.replaceState(null, '', window.location.pathname);
  }, []);

  useEffect(() => {
    const restoreTheme = forceLandingLightTheme();
    document.title = PAGE_TITLE;

    const setMeta = (attr: 'name' | 'property', key: string, content: string) => {
      let meta = document.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attr, key);
        document.head.appendChild(meta);
      }
      meta.content = content;
    };
    setMeta('name', 'description', PAGE_DESCRIPTION);
    setMeta('property', 'og:title', PAGE_TITLE);
    setMeta('property', 'og:description', PAGE_DESCRIPTION);

    const onScroll = () => setShowBackToTop(window.scrollY > 480);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });

    const hash = window.location.hash;
    if (hash) {
      requestAnimationFrame(() => {
        document.querySelector(hash)?.scrollIntoView({ behavior: 'smooth' });
      });
    }

    return () => {
      window.removeEventListener('scroll', onScroll);
      restoreTheme();
    };
  }, []);

  return (
    <div className="landing-page-root min-h-screen w-full overflow-x-hidden bg-white font-[Inter,system-ui,sans-serif] text-slate-950 antialiased">
      <SiteHeader onOpenDemo={openDemo} onOpenDiagnosis={openDiagnosis} />

      <main>
        <SiteHeroSection onOpenDemo={openDemo} onOpenDiagnosis={openDiagnosis} />
        <SiteTrustMetricsBar />
        <SiteSolutionsSection />
        <SiteScoreSection />
        <SitePlatformSection />
        <SiteSecuritySection />
        <SiteContactSection onOpenDemo={openDemo} onOpenDiagnosis={openDiagnosis} />
      </main>

      <SiteFooter />

      <LeadCaptureModal
        open={leadModalOpen}
        intent={leadIntent}
        onClose={() => setLeadModalOpen(false)}
      />

      <button
        type="button"
        onClick={scrollToTop}
        aria-label="Voltar ao topo"
        className={`lp-back-to-top fixed bottom-6 right-6 z-50 flex h-11 w-11 items-center justify-center rounded-full border shadow-sm transition-all ${
          showBackToTop ? 'pointer-events-auto translate-y-0 opacity-100' : 'pointer-events-none translate-y-3 opacity-0'
        }`}
      >
        <ArrowUp className="h-5 w-5" strokeWidth={1.75} />
      </button>
    </div>
  );
};

export default LandingPage;
